import {
  DateRange,
  MultiInputDateTimeRangeField,
} from "@mui/x-date-pickers-pro";

interface Props {
  id: string;
  inputErrors: { [code: string]: boolean };
  setInputErrors: (inputErrors: { [code: string]: boolean }) => void;
  disabled: boolean | undefined;
  requireValue: boolean;
  start: number;
  end: number;
  plannedStart: number;
  update: (updatedData: { start: number; end: number; id: string }) => void;
}

const StartEndTimeFields = ({
  id,
  inputErrors,
  setInputErrors,
  disabled,
  requireValue,
  start,
  end,
  plannedStart,
  update,
}: Props) => {
  const changeTime = (newValue: DateRange<Date> | null) => {
    // If invalid date range, return
    if (!newValue || !newValue[0] || !newValue[1]) return;

    let startDate = newValue[0];
    let endDate = newValue[1];

    // Ensure start date does not change its day (fix to the original day)
    const originalStartDay = new Date(start).setHours(0, 0, 0, 0); // Reference for the original start day

    // Keep startDate on the same day as the original start
    const startDay = new Date(startDate).setHours(0, 0, 0, 0);
    if (startDay !== originalStartDay) {
      startDate = new Date(originalStartDay); // Reset to the same day
      startDate.setHours(
        newValue[0].getHours(),
        newValue[0].getMinutes(),
        0,
        0,
      );
    }

    // Handle night shifts spanning across two days
    let startDateWithTime = startDate.getTime();
    let endDateWithTime = endDate.getTime();

    // If the end time is earlier or the same as the start time, it means the shift spans to the next day
    if (endDateWithTime <= startDateWithTime) {
      endDate.setDate(endDate.getDate() + 1); // Move end date to the next day
      endDateWithTime = endDate.getTime(); // Update the end time value
    }

    // If the shift duration is more than 24 hours, reduce the end date by one day
    const maxShiftDuration = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    if (endDateWithTime - startDateWithTime > maxShiftDuration) {
      endDate.setDate(endDate.getDate() - 1); // Reduce the end date by one day
      endDateWithTime = endDate.getTime(); // Update the end time value
    }

    const newStart = startDate.getTime();
    const newEnd = endDate.getTime();

    // Check if the resulting dates are valid
    if (!isNaN(newStart) && !isNaN(newEnd)) {
      update &&
        update({
          start: newStart,
          end: newEnd,
          id: id,
        });

      // Remove the input error from the array if it exists
      let errors = inputErrors;
      if (errors[id]) {
        delete errors[id];
        setInputErrors && setInputErrors({ ...errors });
      }
    } else {
      // Handle the case where the resulting dates are invalid
      let errors = inputErrors;
      errors[id] = true;
      setInputErrors && setInputErrors({ ...errors });
    }
  };

  return (
    <MultiInputDateTimeRangeField
      format={"HH:mm"}
      value={[new Date(start), new Date(end)] || null}
      onChange={(newValue) => {
        changeTime(newValue);
      }}
      disabled={disabled}
      paddingLeft={"0px"}
      slotProps={{
        textField: {
          size: "small",
          inputProps: {
            style: {
              padding: "5px 10px",
              border: inputErrors[id] ? "1px solid red" : "none",
            },
          },
        },
      }}
    />
  );
};

export default StartEndTimeFields;
